import React, { useEffect, useState } from 'react';
import { DailyQuest } from '@/models/DailyQuest';
import { DailyQuestCard } from './DailyQuestCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { dailyQuestService } from '@/services/DailyQuestService';
import { Skeleton } from '@/components/ui/skeleton';

export const DailyQuestList: React.FC = () => {
  const [quests, setQuests] = useState<DailyQuest[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchQuests = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        const availableQuests = await dailyQuestService.getDailyQuestsForUser(user.id);
        setQuests(availableQuests);
      } catch (error) {
        console.error('Error fetching daily quests:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuests();
  }, [user?.id]);

  const handleStartQuest = (quest: DailyQuest) => {
    if (!quest.lesson) return;
    
    // Store lesson in sessionStorage to be picked up by the learn screen
    sessionStorage.setItem('selectedLesson', JSON.stringify(quest.lesson));
    
    // Navigate to learn screen
    navigate('/learn');
  };

  if (loading) {
    return (
      <div className="space-y-4 w-full max-w-sm mx-auto">
        <h2 className="text-xl font-bold">Daily Quests</h2>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex flex-col space-y-3">
              <Skeleton className="h-[125px] w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (quests.length === 0) {
    return (
      <div className="space-y-4 w-full max-w-sm mx-auto">
        <h2 className="text-xl font-bold">Daily Quests</h2>
        <div className="p-4 border rounded-lg bg-muted/10">
          <p className="text-center text-muted-foreground">
            No daily quests available. Complete more lessons to unlock daily quests.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-sm mx-auto">
      <h2 className="text-xl font-bold">Daily Quests</h2>
      <div className="space-y-3">
        {quests.map((quest) => (
          <DailyQuestCard 
            key={quest.id} 
            quest={quest} 
            onStartQuest={handleStartQuest} 
          />
        ))}
      </div>
    </div>
  );
};
